"use client";

import { Link } from "react-router-dom";
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/axios";
import { DateRangePicker } from "@/components/reports/DateRangePicker";
import { Button, buttonVariants } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { ArrowLeft } from "lucide-react";

type Row = { label: string; revenue: number; cogs: number; gross_profit: number };

export default function ProfitReportPage() {
  const defTo = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const defFrom = useMemo(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 3);
    return d.toISOString().slice(0, 10);
  }, []);
  const [from, setFrom] = useState(defFrom);
  const [to, setTo] = useState(defTo);
  const [group, setGroup] = useState<"month" | "category" | "product">("month");

  const { data, refetch } = useQuery({
    queryKey: ["reports", "profit", from, to, group],
    queryFn: async () => {
      const { data: d } = await api.get<{ group: string; rows: Row[]; totals: { revenue: number; cogs: number; gross_profit: number } }>(
        "/api/reports/profit-loss",
        { params: { from, to, group } },
      );
      return d;
    },
  });

  return (
    <div className="space-y-6 p-6 max-w-4xl">
      <Link
        to="/reports"
        className={cn(buttonVariants({ variant: "ghost", size: "sm" }), "gap-2 inline-flex")}
      >
        <ArrowLeft className="h-4 w-4" />
        Reports
      </Link>
      <h1 className="text-2xl font-semibold tracking-tight">P&L</h1>
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Range & breakdown</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <DateRangePicker from={from} to={to} onFromChange={setFrom} onToChange={setTo} />
          <div className="flex flex-wrap gap-2">
            {(["month", "category", "product"] as const).map((g) => (
              <Button
                key={g}
                type="button"
                size="sm"
                variant={group === g ? "default" : "outline"}
                onClick={() => setGroup(g)}
              >
                {g}
              </Button>
            ))}
            <Button type="button" variant="secondary" size="sm" onClick={() => void refetch()}>
              Apply
            </Button>
          </div>
        </CardContent>
      </Card>

      {data?.totals && (
        <div className="flex flex-wrap gap-4 text-sm font-medium">
          <span>Revenue ₹{data.totals.revenue.toFixed(2)}</span>
          <span>COGS ₹{data.totals.cogs.toFixed(2)}</span>
          <span>Gross ₹{data.totals.gross_profit.toFixed(2)}</span>
        </div>
      )}

      <Card>
        <CardContent className="pt-6 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-muted-foreground">
                <th className="p-2">Label</th>
                <th className="p-2 text-right">Revenue</th>
                <th className="p-2 text-right">COGS</th>
                <th className="p-2 text-right">Gross</th>
              </tr>
            </thead>
            <tbody>
              {(data?.rows ?? []).map((r) => (
                <tr key={r.label} className="border-t">
                  <td className="p-2 font-medium">{r.label}</td>
                  <td className="p-2 text-right tabular-nums">{r.revenue.toFixed(2)}</td>
                  <td className="p-2 text-right tabular-nums">{r.cogs.toFixed(2)}</td>
                  <td className="p-2 text-right tabular-nums">{r.gross_profit.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
